import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { shareReplay, take } from 'rxjs/operators';
import { AgentsService } from './agents.service';
import { ServicesFonctionnelsService } from './services-fonctionnels.service';
import { DirectionService } from './direction.service';
import { PostesService } from './postes.service';

@Injectable({
  providedIn: 'root'
})
export class SharedFilterDataService {
  // Subjects to store and provide data
  private agentsSubject = new Subject<any[]>();
  private agentsDemandesSubject = new Subject<any[]>();
  private servicesSubject = new Subject<any[]>();
  private directionsSubject = new Subject<any[]>();
  private postesSubject = new Subject<any[]>();
  
  // Store current values since Subject doesn't hold last value
  private agentsValue: any[] = [];
  private agentsDemandesValue: any[] = [];
  private servicesValue: any[] = [];
  private directionsValue: any[] = [];
  private postesValue: any[] = [];

  // Flags to track pending requests
  private agentsLoading = false;
  private agentsDemandesLoading = false;
  private servicesLoading = false;
  private directionsLoading = false;
  private postesLoading = false;

  // Public observables for components to subscribe to
  public agents$ = this.agentsSubject.asObservable().pipe(shareReplay(1));
  public agentsDemandes$ = this.agentsDemandesSubject.asObservable().pipe(shareReplay(1));
  public services$ = this.servicesSubject.asObservable().pipe(shareReplay(1));
  public directions$ = this.directionsSubject.asObservable().pipe(shareReplay(1));
  public postes$ = this.postesSubject.asObservable().pipe(shareReplay(1));

  constructor(
    private readonly agentsService: AgentsService,
    private readonly servicesFonctionnelsService: ServicesFonctionnelsService,
    private readonly directionService: DirectionService,
    private readonly postesService: PostesService
  ) {}

  /**
   * Loads agents if not already loaded
   * @returns Observable of agents
   */
  loadAgents(): Observable<any[]> {
    // Only load if data is empty and not already loading
    if (this.agentsValue.length === 0 && !this.agentsLoading) {
      this.agentsLoading = true;

      this.agentsService
        .getAllAgentsDemandes('consultation')
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            if (data && Array.isArray(data)) {
              this.agentsValue = data;
              this.agentsSubject.next(data);
            }
            this.agentsLoading = false;
          },
          error: (err) => {
            this.agentsLoading = false;
          }
        });
    }

    return this.agents$;
  }

  /**
   * Loads agents referenced by demands accessible to the current user.
   */
  loadAgentsDemandes(): Observable<any[]> {
    if (this.agentsDemandesValue.length === 0 && !this.agentsDemandesLoading) {
      this.agentsDemandesLoading = true;

      this.agentsService
        .getAllAgentsDemandesPourFiltre()
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            if (data && Array.isArray(data)) {
              this.agentsDemandesValue = data;
              this.agentsDemandesSubject.next(data);
            }
            this.agentsDemandesLoading = false;
          },
          error: () => {
            this.agentsDemandesLoading = false;
          }
        });
    }

    return this.agentsDemandes$;
  }

  /**
   * Loads services if not already loaded
   * @returns Observable of services
   */
  loadServices(): Observable<any[]> {
    // Only load if data is empty and not already loading
    if (this.servicesValue.length === 0 && !this.servicesLoading) {
      this.servicesLoading = true;

      this.servicesFonctionnelsService
        .getAllServicesList()
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            if (data && Array.isArray(data)) {
              this.servicesValue = data;
              this.servicesSubject.next(data);
            }
            this.servicesLoading = false;
          },
          error: (err) => {
            this.servicesLoading = false;
          }
        });
    }

    return this.services$;
  }

  /**
   * Loads directions if not already loaded
   * @returns Observable of directions
   */
  loadDirections(): Observable<any[]> {
    // Only load if data is empty and not already loading
    if (this.directionsValue.length === 0 && !this.directionsLoading) {
      this.directionsLoading = true;

      this.directionService
        .getAllDirectionsDemandes()
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            if (data && Array.isArray(data)) {
              this.directionsValue = data;
              this.directionsSubject.next(data);
            }
            this.directionsLoading = false;
          },
          error: (err) => {
            this.directionsLoading = false;
          }
        });
    }

    return this.directions$;
  }

  /**
   * Loads postes if not already loaded
   * @returns Observable of postes
   */
  loadPostes(): Observable<any[]> {
    // Only load if data is empty and not already loading
    if (this.postesValue.length === 0 && !this.postesLoading) {
      this.postesLoading = true;

      this.postesService
        .getAllPostes()
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            if (data && Array.isArray(data)) {
              this.postesValue = data;
              this.postesSubject.next(data);
            }
            this.postesLoading = false;
          },
          error: (err) => {
            this.postesLoading = false;
          }
        });
    }

    return this.postes$;
  }

  /**
   * Gets the current value of the agents
   */
  getAgents(): any[] {
    return this.agentsValue;
  }

  /**
   * Gets the current value of the services
   */
  getServices(): any[] {
    return this.servicesValue;
  }

  /**
   * Gets the current value of the directions
   */
  getDirections(): any[] {
    return this.directionsValue;
  }

  /**
   * Gets the current value of the postes
   */
  getPostes(): any[] {
    return this.postesValue;
  }

  /**
   * Refreshes all data
   */
  refreshAll(): void {
    // Clear values to force reload
    this.agentsValue = [];
    this.agentsDemandesValue = [];
    this.servicesValue = [];
    this.directionsValue = [];
    this.postesValue = [];

    // Load data again
    this.loadAgents();
    this.loadAgentsDemandes();
    this.loadServices();
    this.loadDirections();
    this.loadPostes();
  }
}
